import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, X } from 'lucide-react';
import { useState } from 'react';

import { queryKeys } from '../../hooks/useApi';
import { getMutationErrorMessage } from '../../lib/apiErrors';
import {
    canRemoveRoleFromManage,
    getAddableRoles,
    getLoginRegrantReason,
    getRoleDescription,
} from '../../lib/organizationRoles';
import { useAccessApi } from '../../services/api';
import { Button } from '../ui/button';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { Select } from '../ui/select';
import { cardTitleClassName } from '../ui/typography';

const OUTSIDE_MANAGE_REASON = 'Managed in Auth0 or Terraform, not from Manage.';

function RoleChip({
    role,
    lockedReason,
    removing,
    disabled,
    onRemove,
}: {
    role: string;
    lockedReason: string | null;
    removing: boolean;
    disabled: boolean;
    onRemove: () => void;
}) {
    const description = getRoleDescription(role);
    return (
        <li
            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white py-0.5 pl-2.5 pr-1 text-xs font-medium text-neutral-800"
            title={[description, lockedReason].filter(Boolean).join(' ') || undefined}
        >
            <span className={removing ? 'opacity-50' : undefined}>{role}</span>
            {lockedReason ? (
                <Lock className="mx-1 size-3 text-neutral-400" aria-label={`${role} is locked`} />
            ) : (
                <button
                    type="button"
                    onClick={onRemove}
                    disabled={disabled}
                    aria-label={`Remove ${role}`}
                    className="-my-1 -mr-1.5 inline-flex size-6 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <X className="size-3.5" aria-hidden />
                </button>
            )}
        </li>
    );
}

/** View, assign and remove a member's Auth0 roles in one organization. */
export function OrganizationRolesEditor({
    orgId,
    userId,
    userName,
    roles,
    onRolesChange,
}: {
    orgId: string;
    userId: string;
    userName: string;
    roles: string[];
    onRolesChange: (roles: string[]) => void;
}) {
    const { setOrganizationUserRole } = useAccessApi();
    const queryClient = useQueryClient();
    const [roleToAdd, setRoleToAdd] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isRemoveAdminConfirmOpen, setIsRemoveAdminConfirmOpen] = useState(false);

    const mutation = useMutation({
        mutationFn: ({ roleName, assigned }: { roleName: string; assigned: boolean }) =>
            setOrganizationUserRole(orgId, userId, roleName, assigned),
        onMutate: () => setError(null),
        onSuccess: (data, { roleName }) => {
            setRoleToAdd('');
            onRolesChange(data.roles);
            // Company Users tabs list every member's roles and admin status.
            void queryClient.invalidateQueries({
                queryKey: [...queryKeys.all, 'admin-company-admins'],
            });
            if (roleName === 'organization_admin') {
                // The company admin flow also resets the user's team roles.
                void queryClient.invalidateQueries({ queryKey: [...queryKeys.all, 'admin-users'] });
                void queryClient.invalidateQueries({
                    queryKey: [...queryKeys.all, 'admin-company-teams'],
                });
            }
        },
        onError: (err, { roleName, assigned }) => {
            setError(
                getMutationErrorMessage(
                    err,
                    `Could not ${assigned ? 'assign' : 'remove'} ${roleName}.`,
                ),
            );
        },
    });

    const sortedRoles = [...roles].sort();
    const addableRoles = getAddableRoles(roles);
    const pendingRole = mutation.isPending ? mutation.variables?.roleName : undefined;
    const regrantNotes = sortedRoles.flatMap((role) => {
        const reason = getLoginRegrantReason(role, roles);
        return reason ? [{ role, reason }] : [];
    });
    const hasOutsideManageRoles = sortedRoles.some(
        (role) => !canRemoveRoleFromManage(role, roles) && !getLoginRegrantReason(role, roles),
    );

    const removeRole = (role: string) => {
        if (role === 'organization_admin') {
            setIsRemoveAdminConfirmOpen(true);
            return;
        }
        mutation.mutate({ roleName: role, assigned: false });
    };

    return (
        <section aria-labelledby="organization-roles-heading" className="space-y-3">
            <div>
                <h2 id="organization-roles-heading" className={cardTitleClassName}>
                    Auth0 roles in this organization
                </h2>
                <p className="mt-1 text-xs text-neutral-500">
                    Changes apply at the user&apos;s next sign-in or token refresh.
                </p>
            </div>

            {sortedRoles.length > 0 ? (
                <ul className="flex flex-wrap gap-1.5" aria-label="Assigned roles">
                    {sortedRoles.map((role) => (
                        <RoleChip
                            key={role}
                            role={role}
                            lockedReason={
                                canRemoveRoleFromManage(role, roles)
                                    ? null
                                    : (getLoginRegrantReason(role, roles) ?? OUTSIDE_MANAGE_REASON)
                            }
                            removing={pendingRole === role}
                            disabled={mutation.isPending}
                            onRemove={() => removeRole(role)}
                        />
                    ))}
                </ul>
            ) : (
                <p className="text-sm text-neutral-500">No roles assigned</p>
            )}

            {regrantNotes.length > 0 || hasOutsideManageRoles ? (
                <ul className="space-y-0.5 text-xs text-neutral-500">
                    {regrantNotes.map(({ role, reason }) => (
                        <li key={role}>
                            <span className="font-medium text-neutral-700">{role}</span>: {reason}
                        </li>
                    ))}
                    {hasOutsideManageRoles ? (
                        <li>Other locked roles: {OUTSIDE_MANAGE_REASON}</li>
                    ) : null}
                </ul>
            ) : null}

            {addableRoles.length > 0 ? (
                <div className="flex max-w-xl flex-col gap-2 sm:flex-row sm:items-start">
                    <div className="min-w-0 flex-1">
                        <Select
                            ariaLabel="Role to add"
                            placeholder="Choose a role to add"
                            value={roleToAdd}
                            onValueChange={setRoleToAdd}
                            disabled={mutation.isPending}
                            searchable
                            searchPlaceholder="Search roles"
                            options={addableRoles.map((role) => {
                                const description = getRoleDescription(role);
                                return {
                                    value: role,
                                    label: description ? `${role} · ${description}` : role,
                                };
                            })}
                        />
                    </div>
                    <Button
                        className="self-start"
                        type="button"
                        variant="outline"
                        disabled={!roleToAdd || mutation.isPending}
                        loading={mutation.isPending && mutation.variables?.assigned === true}
                        onClick={() => mutation.mutate({ roleName: roleToAdd, assigned: true })}
                    >
                        {mutation.isPending && mutation.variables?.assigned
                            ? 'Adding…'
                            : 'Add role'}
                    </Button>
                </div>
            ) : null}

            {error ? (
                <p role="alert" className="text-sm text-destructive">
                    {error}
                </p>
            ) : null}
            <ConfirmDialog
                open={isRemoveAdminConfirmOpen}
                title={`Remove organization_admin from ${userName}?`}
                description="Every active team role for this user in this company will reset to Pro User."
                confirmLabel="Remove role"
                destructive
                onCancel={() => setIsRemoveAdminConfirmOpen(false)}
                onConfirm={() => {
                    setIsRemoveAdminConfirmOpen(false);
                    mutation.mutate({ roleName: 'organization_admin', assigned: false });
                }}
            />
        </section>
    );
}
